import React,{useEffect,useMemo,useState}from"react";
import{Box,Button,FormControl,InputLabel,MenuItem,Paper,Select,Table,TableBody,TableCell,TableContainer,TableHead,TablePagination,TableRow,TextField,Typography,useMediaQuery}from"@mui/material";
import{useLocation,useNavigate,useParams}from"react-router-dom";
import Sidebar from"./AdminPanel/Sidebar";
import TopBar from"./AdminPanel/TopBar";
import PayrollForm from"./AdminPayroll/PayrollForm";
import PayrollPayments from"./AdminPayroll/PayrollPayments";
import{useDashboardTheme}from"../../context/DashboardThemeContext";
import{useAuth}from"../../context/AuthContext";
import{useAlert}from"../../context/AlertContext";

const API_BASE="/api/purchases/nomina";

function getToken(){
  return localStorage.getItem("accessToken")||localStorage.getItem("access")||"";
}

function userCan(user,code){
  if(!user)return false;
  if(user.role==="super_admin"||user.role==="admin")return true;
  return(user.permissions||[]).includes(code);
}

function formatMoney(value){
  return Number(value||0).toFixed(2);
}

function formatDate(value){
  if(!value)return"-";
  const parts=String(value).split("-");
  if(parts.length===3)return`${parts[2]}/${parts[1]}/${parts[0]}`;
  return value;
}

function employeeName(employee){
  if(!employee)return"";
  return employee.razon_social||employee.nombre_comercial||"";
}

export default function AdminPayroll(){
  const[sidebarOpen,setSidebarOpen]=useState(true);
  const{dashboardTheme}=useDashboardTheme();
  const{user}=useAuth();
  const{showError,showSuccess,showInfo}=useAlert();
  const navigate=useNavigate();
  const location=useLocation();
  const{payrollId}=useParams();
  const token=getToken();
  const isMobile=useMediaQuery("(max-width:900px)");
  const isCreate=location.pathname==="/admin-panel/nomina/nueva";
  const isPayments=Boolean(payrollId)&&location.pathname.endsWith("/pagos");
  const isDetail=Boolean(payrollId)&&!isPayments;
  const isList=!isCreate&&!isDetail&&!isPayments;
  const[empresaId,setEmpresaId]=useState("");
  const[empresaLoading,setEmpresaLoading]=useState(true);
  const canView=userCan(user,"compras.view");
  const canCreate=userCan(user,"compras.create");
  const canEdit=userCan(user,"compras.edit");
  const canDelete=userCan(user,"compras.delete");

  const outlinedButtonSx={
    minHeight:40,
    px:2.4,
    borderRadius:"12px",
    textTransform:"none",
    fontWeight:700,
    color:dashboardTheme.button_color,
    borderColor:dashboardTheme.button_color,
    boxShadow:"none",
    "&:hover":{
      borderColor:dashboardTheme.button_color,
      bgcolor:`${dashboardTheme.button_color}12`,
      boxShadow:"none"
    }
  };

  const smallButtonSx={
    minHeight:32,
    px:1.5,
    borderRadius:"8px",
    textTransform:"none",
    fontWeight:600,
    color:dashboardTheme.button_color,
    borderColor:dashboardTheme.button_color,
    boxShadow:"none",
    "&:hover":{
      borderColor:dashboardTheme.button_color,
      bgcolor:`${dashboardTheme.button_color}12`,
      boxShadow:"none"
    }
  };

  const smallContainedButtonSx={
    ...dashboardTheme.buttonStyles.contained,
    minHeight:32,
    px:1.5,
    borderRadius:"8px",
    textTransform:"none",
    fontWeight:600,
    boxShadow:"none"
  };

  const dangerButtonSx={
    ...smallButtonSx,
    color:"#D32F2F",
    borderColor:"#D32F2F",
    "&:hover":{
      borderColor:"#D32F2F",
      bgcolor:"rgba(211,47,47,0.08)",
      boxShadow:"none"
    }
  };

  const selectMenuProps={
    disableScrollLock:true,
    PaperProps:{
      elevation:0,
      sx:{
        mt:.5,
        bgcolor:dashboardTheme.card_background,
        color:dashboardTheme.text_color,
        border:`1px solid ${dashboardTheme.border_color}`,
        boxShadow:"none"
      }
    }
  };

  const parseError=data=>{
    const error=data?.error||data?.detail||data?.mensaje;
    if(!error)return"No se pudo procesar la solicitud.";
    if(typeof error==="string")return error;
    if(typeof error==="object")return Object.values(error).flat().join(" ");
    return"No se pudo procesar la solicitud.";
  };

  const loadEmpresa=async()=>{
    try{
      setEmpresaLoading(true);

      const response=await fetch("/api/enterprise/empresa/obtener/",{
        headers:{Authorization:`Bearer ${token}`}
      });

      const data=await response.json();

      if(!response.ok){
        showError(data.error||data.detail||"No se pudo cargar la empresa.");
        return;
      }

      if(!data?.empresa?.id){
        showError("No existe una empresa configurada.");
        return;
      }

      setEmpresaId(data.empresa.id);
    }catch{
      showError("Error de conexión al cargar la empresa.");
    }finally{
      setEmpresaLoading(false);
    }
  };

  useEffect(()=>{
    loadEmpresa();
  },[]);

  return(
    <Box sx={{minHeight:"100vh",bgcolor:dashboardTheme.dashboard_background,display:"flex",color:dashboardTheme.text_color,overflowX:"hidden"}}>
      <Sidebar open={sidebarOpen} onClose={()=>setSidebarOpen(false)}/>

      <Box sx={{flex:1,minWidth:0,minHeight:"100vh",display:"flex",flexDirection:"column"}}>
        <TopBar sidebarOpen={sidebarOpen} onToggleSidebar={()=>setSidebarOpen(prev=>!prev)}/>

        <Box component="main" sx={{flex:1,width:"100%",p:{xs:1.5,sm:2,md:3}}}>
          <Box sx={{width:{xs:"100%",lg:"95%"},mx:"auto",mt:{xs:1,md:1.5}}}>
            {empresaLoading&&(
              <Typography sx={{py:4,textAlign:"center",color:dashboardTheme.muted_text_color}}>
                Cargando...
              </Typography>
            )}

            {!empresaLoading&&empresaId&&isList&&(
              <PayrollList
                empresaId={empresaId}
                token={token}
                canView={canView}
                canCreate={canCreate}
                canEdit={canEdit}
                dashboardTheme={dashboardTheme}
                navigate={navigate}
                showError={showError}
                smallButtonSx={smallButtonSx}
                smallContainedButtonSx={smallContainedButtonSx}
                selectMenuProps={selectMenuProps}
                isMobile={isMobile}
              />
            )}

            {!empresaLoading&&empresaId&&(isCreate||isDetail)&&(
              <PayrollForm
                empresaId={empresaId}
                payrollId={payrollId}
                isCreate={isCreate}
                token={token}
                canCreate={canCreate}
                canEdit={canEdit}
                canDelete={canDelete}
                dashboardTheme={dashboardTheme}
                navigate={navigate}
                showError={showError}
                showSuccess={showSuccess}
                showInfo={showInfo}
                outlinedButtonSx={outlinedButtonSx}
                dangerButtonSx={dangerButtonSx}
                parseError={parseError}
              />
            )}

            {!empresaLoading&&empresaId&&isPayments&&(
              <PayrollPayments
                empresaId={empresaId}
                payrollId={payrollId}
                token={token}
                canCreate={canCreate}
                canDelete={canDelete}
                dashboardTheme={dashboardTheme}
                navigate={navigate}
                showError={showError}
                showSuccess={showSuccess}
                showInfo={showInfo}
                outlinedButtonSx={outlinedButtonSx}
                dangerButtonSx={dangerButtonSx}
                parseError={parseError}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function PayrollList({
  empresaId,
  token,
  canView,
  canCreate,
  canEdit,
  dashboardTheme,
  navigate,
  showError,
  smallButtonSx,
  smallContainedButtonSx,
  selectMenuProps,
  isMobile
}){
  const[items,setItems]=useState([]);
  const[loading,setLoading]=useState(false);
  const[search,setSearch]=useState("");
  const[estado,setEstado]=useState("todos");
  const[fechaDesde,setFechaDesde]=useState("");
  const[fechaHasta,setFechaHasta]=useState("");
  const[page,setPage]=useState(0);
  const rowsPerPage=isMobile?10:30;

  const loadPayroll=async()=>{
    if(!canView)return;

    try{
      setLoading(true);

      const params=new URLSearchParams({empresa_id:empresaId});

      if(search.trim())params.append("q",search.trim());
      if(estado!=="todos")params.append("estado",estado);
      if(fechaDesde)params.append("fecha_desde",fechaDesde);
      if(fechaHasta)params.append("fecha_hasta",fechaHasta);

      const response=await fetch(`${API_BASE}/?${params.toString()}`,{
        headers:{Authorization:`Bearer ${token}`}
      });

      const data=await response.json();

      if(!response.ok){
        showError(data.error||data.detail||"No se pudo cargar la nómina.");
        setItems([]);
        return;
      }

      setItems(data.results||[]);
    }catch{
      setItems([]);
      showError("Error de conexión.");
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{
    const delay=setTimeout(()=>{
      setPage(0);
      loadPayroll();
    },350);

    return()=>clearTimeout(delay);
  },[search,estado,fechaDesde,fechaHasta,empresaId,canView]);

  useEffect(()=>{
    setPage(0);
  },[rowsPerPage]);

  const visibleItems=useMemo(()=>{
    const start=page*rowsPerPage;
    return items.slice(start,start+rowsPerPage);
  },[items,page,rowsPerPage]);

  const secondaryText=dashboardTheme.theme_mode==="dark"?"rgba(255,255,255,0.62)":"#64748b";

  return(
    <>
      <Box sx={{display:"flex",alignItems:{xs:"stretch",sm:"center"},justifyContent:"space-between",flexDirection:{xs:"column",sm:"row"},gap:1.5,mb:{xs:1.5,md:2}}}>
        <Typography sx={{fontSize:{xs:24,sm:26,md:30},fontWeight:600,color:dashboardTheme.primary_color,lineHeight:1.2}}>
          Nómina
        </Typography>

        {canCreate&&(
          <Button
            variant="contained"
            onClick={()=>navigate("/admin-panel/nomina/nueva")}
            sx={dashboardTheme.buttonStyles.contained}
          >
            Nueva nómina
          </Button>
        )}
      </Box>

      <Paper elevation={0} sx={dashboardTheme.tableStyles.wrapper}>
        <Box sx={{...dashboardTheme.tableStyles.toolbar,display:"flex",flexWrap:"wrap",alignItems:"center",gap:1}}>
          <TextField
            fullWidth
            size="small"
            label="Buscar empleado o concepto"
            value={search}
            onChange={e=>setSearch(e.target.value)}
            sx={{maxWidth:{xs:"100%",md:390},...dashboardTheme.inputStyles}}
          />

          <FormControl size="small" sx={{minWidth:{xs:"100%",sm:160},...dashboardTheme.inputStyles}}>
            <InputLabel>Estado</InputLabel>

            <Select
              label="Estado"
              value={estado}
              onChange={e=>setEstado(e.target.value)}
              MenuProps={selectMenuProps}
            >
              <MenuItem value="todos">Todos</MenuItem>
              <MenuItem value="REGISTRADO">Registrado</MenuItem>
              <MenuItem value="ANULADO">Anulado</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            type="date"
            label="Desde"
            value={fechaDesde}
            onChange={e=>setFechaDesde(e.target.value)}
            InputLabelProps={{shrink:true}}
            sx={{minWidth:{xs:"100%",sm:155},...dashboardTheme.inputStyles}}
          />

          <TextField
            size="small"
            type="date"
            label="Hasta"
            value={fechaHasta}
            onChange={e=>setFechaHasta(e.target.value)}
            InputLabelProps={{shrink:true}}
            sx={{minWidth:{xs:"100%",sm:155},...dashboardTheme.inputStyles}}
          />
        </Box>

        <Box sx={dashboardTheme.tableStyles.tableArea}>
          <TableContainer sx={dashboardTheme.tableStyles.container}>
            <Table size="small" sx={dashboardTheme.tableStyles.table}>
              <TableHead>
                <TableRow sx={dashboardTheme.tableStyles.headRow}>
                  <TableCell sx={{...dashboardTheme.tableStyles.headCell,minWidth:220}}>Empleado</TableCell>
                  <TableCell sx={{...dashboardTheme.tableStyles.headCell,minWidth:170}}>Período</TableCell>
                  <TableCell sx={dashboardTheme.tableStyles.headCell}>Fecha</TableCell>
                  <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Total</TableCell>
                  <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Pagado</TableCell>
                  <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Saldo</TableCell>
                  <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Acciones</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {visibleItems.map(item=>(
                  <TableRow
                    key={item.id}
                    hover
                    sx={{...dashboardTheme.tableStyles.bodyRow,cursor:"pointer"}}
                    onDoubleClick={()=>navigate(`/admin-panel/nomina/${item.id}`)}
                  >
                    <TableCell>
                      <Typography sx={{fontSize:14,fontWeight:600,color:dashboardTheme.text_color,lineHeight:1.25}}>
                        {employeeName(item.empleado)||"-"}
                      </Typography>

                      {item.empleado?.identificacion&&(
                        <Typography sx={{fontSize:11.5,fontWeight:500,color:secondaryText,mt:.25}}>
                          {item.empleado.identificacion}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{whiteSpace:"nowrap"}}>
                      {formatDate(item.periodo_desde)} - {formatDate(item.periodo_hasta)}
                    </TableCell>

                    <TableCell sx={{whiteSpace:"nowrap"}}>
                      {formatDate(item.fecha_nomina)}
                    </TableCell>

                    <TableCell align="right" sx={{whiteSpace:"nowrap"}}>
                      <Typography sx={{fontSize:13.5,fontWeight:700,color:dashboardTheme.text_color}}>
                        ${formatMoney(item.total)}
                      </Typography>
                    </TableCell>

                    <TableCell align="right" sx={{whiteSpace:"nowrap"}}>
                      ${formatMoney(item.total_pagado)}
                    </TableCell>

                    <TableCell align="right" sx={{whiteSpace:"nowrap"}}>
                      <Typography sx={{
                        fontSize:13.5,
                        fontWeight:700,
                        color:Number(item.saldo_pendiente)>0?"#D97706":dashboardTheme.text_color
                      }}>
                        ${formatMoney(item.saldo_pendiente)}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Box sx={{display:"flex",justifyContent:"flex-end",gap:.7}}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={e=>{
                            e.stopPropagation();
                            navigate(`/admin-panel/nomina/${item.id}`);
                          }}
                          sx={smallButtonSx}
                        >
                          {item.estado!=="ANULADO"&&canEdit?"Editar":"Ver"}
                        </Button>

                        {item.estado==="REGISTRADO"&&(
                          <Button
                            variant="contained"
                            size="small"
                            onClick={e=>{
                              e.stopPropagation();
                              navigate(`/admin-panel/nomina/${item.id}/pagos`);
                            }}
                            sx={smallContainedButtonSx}
                          >
                            Pagos
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}

                {!loading&&visibleItems.length===0&&canView&&(
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={dashboardTheme.tableStyles.emptyCell}>
                      No hay registros de nómina
                    </TableCell>
                  </TableRow>
                )}

                {loading&&(
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={dashboardTheme.tableStyles.emptyCell}>
                      Cargando nómina...
                    </TableCell>
                  </TableRow>
                )}

                {!canView&&(
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={dashboardTheme.tableStyles.emptyCell}>
                      No tienes permisos para ver nómina
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <TablePagination
          component="div"
          count={items.length}
          page={page}
          onPageChange={(_event,newPage)=>setPage(newPage)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({from,to,count})=>`${from}-${to} de ${count}`}
          sx={dashboardTheme.tableStyles.pagination}
        />
      </Paper>
    </>
  );
}